import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { rupee } from "@/lib/api";
import { X, MapPin, Clock, Store, Check, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProductOffers({ group, onClose }) {
  const nav = useNavigate();
  const { addItem, qtyOf } = useCart();
  if (!group) return null;

  const add = (offer) => {
    const product = { id: offer.product_id, name: group.name, price: offer.price, mrp: offer.mrp, image: group.image, unit: group.unit, category: group.category };
    const store = { id: offer.store_id, name: offer.store_name, delivery_fee: offer.delivery_fee };
    const res = addItem(product, store);
    if (res.conflict) toast.error("Your cart has items from another store. Clear it to add from a new store.");
    else toast.success(`Added from ${offer.store_name}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" data-testid="product-offers-modal" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start gap-3">
          {group.image && <img src={group.image} alt="" className="h-16 w-16 rounded-xl object-cover" />}
          <div className="flex-1">
            <p className="font-head text-lg font-bold text-slate-900">{group.name}</p>
            <p className="text-xs text-slate-400">{group.brand} · {group.unit}</p>
            <p className="mt-1 text-sm font-semibold text-brand">{group.offer_count} nearby {group.offer_count === 1 ? "store" : "stores"} · from {rupee(group.best_price)}</p>
          </div>
          <button onClick={onClose} data-testid="offers-close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        <p className="mb-2 text-xs font-bold uppercase text-slate-400">Choose a store</p>
        <div className="space-y-2">
          {group.offers.map((o) => {
            const inCart = qtyOf(o.product_id) > 0;
            return (
              <div key={o.product_id} data-testid="offer-row" className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3">
                <div className="flex-1 min-w-0">
                  <button onClick={() => nav(`/store/${o.store_slug || o.store_id}`)} className="flex items-center gap-1.5 text-sm font-bold text-slate-800 hover:text-brand">
                    <Store className="h-3.5 w-3.5 text-brand" />{o.store_name}
                  </button>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                    {o.distance_km != null && o.distance_km <= 50 && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{o.distance_km} km</span>}
                    {o.eta_min && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{o.eta_min}–{o.eta_max} min</span>}
                    {o.stock <= 5 && <span className="font-semibold text-amber-warm">Only {o.stock} left</span>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-black text-slate-900">{rupee(o.price)}</p>
                  {o.discount_pct > 0 && <p className="font-mono text-[11px] text-slate-400 line-through">{rupee(o.mrp)}</p>}
                </div>
                <button onClick={() => add(o)} data-testid="offer-add-btn" className={`rounded-xl px-3 py-2 text-sm font-bold ${inCart ? "bg-brand-soft text-brand" : "bg-brand text-white"}`}>
                  {inCart ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
